import {createAction} from 'Shared/utilities/actionCreator';

export enum SchooxUserActionType {
  GET_AND_SYNC_SCHOOX_USER = 'schoox/GET_AND_SYNC_SCHOOX_USER',
  TOGGLE_SCHOOX_CONFIRMATION = 'schoox/TOGGLE_SCHOOX_CONFIRMATION',
  IS_POPUP_CLOSED = 'schoox/IS_POPUP_CLOSED',
  REDIRECT_SCHOOX = 'schoox/REDIRECT_SCHOOX',
  IS_LOADING = 'schoox/IS_LOADING',
  IS_COMPLETE = 'schoox/IS_COMPLETE',
  IS_SYNCED = 'schoox/IS_SYNCED',
  SET_PERCENTAGE = 'schoox/SET_PERCENTAGE',
  PERSIST_SCHOOX_USER = 'schoox/PERSIST_SCHOOX_USER',
}

export type SchooxState = {
  schooxUser: SchooxUser;
  isSchooxConfirmationOpen: boolean;
  isLoading: boolean;
  isSynced: boolean;
  isComplete: boolean;
  loadingMessage?: string;
  username: string;
  percentage?: number;
  isSchooxPopupClosed: boolean;
};

export type SchooxUser = {
  isSynced: boolean;
  brokerages: Brokerage[];
  email: string;
  firstName: string;
  isInternal: boolean;
  isSyncedWithSchooxSource: boolean;
  isExistingSchooxUser: boolean;
  lastName: string;
  userName: string;
  uuid: string;
  brokerageUnitIds: number[];
  totalIds: number;
};

export type Brokerage = {
  brokerageName: string;
  territories: Territory[];
  brokerageUnitId: number;
  territoriesUnitIds: number[];
};

export type Territory = {
  territory: string;
  regions: Region[];
  territoryUnitId: number;
  regionsUnitIds: number[];
};

export type Region = {
  region: string;
  recruiterCodes: RecruiterCode[];
  regionUnitId: number;
  recruiterCodesUnitIds: number[];
};

export type RecruiterCode = {
  recruiterCode: string;
  agentIds: AgentId[];
  recruiterCodeUnitId: number;
  agentIdsUnitIds: number[];
};

export type AgentId = {
  agentId: string;
  agentUnitId: number;
};

export type SchooxAgentAboveIds = {
  agentAboveIds: number[];
  brokerageName: string;
};

export const DEFAULT_AGENT_ID_STATE: AgentId = {
  agentId: '',
  agentUnitId: 0,
};

export const DEFAULT_RECRUITER_STATE: RecruiterCode = {
  recruiterCode: '',
  agentIds: [DEFAULT_AGENT_ID_STATE],
  recruiterCodeUnitId: 0,
  agentIdsUnitIds: [],
};

export const DEFAULT_REGION_STATE: Region = {
  region: '',
  recruiterCodes: [DEFAULT_RECRUITER_STATE],
  regionUnitId: 0,
  recruiterCodesUnitIds: [],
};

export const DEFAULT_TERR_STATE: Territory = {
  territory: '',
  regions: [DEFAULT_REGION_STATE],
  territoryUnitId: 0,
  regionsUnitIds: [],
};

export const DEFAULT_BROKERAGE_STATE: Brokerage = {
  brokerageUnitId: 0,
  territoriesUnitIds: [],
  brokerageName: '',
  territories: [DEFAULT_TERR_STATE],
};

export const DEFAULT_SCHOOX_USER_STATE: SchooxUser = {
  isSynced: false,
  brokerages: [DEFAULT_BROKERAGE_STATE],
  email: '',
  firstName: '',
  isInternal: false,
  isSyncedWithSchooxSource: false,
  isExistingSchooxUser: false,
  lastName: '',
  userName: '',
  uuid: '',
  brokerageUnitIds: [],
  totalIds: 0,
};

export const DEFAULT_STATE: SchooxState = {
  schooxUser: DEFAULT_SCHOOX_USER_STATE,
  isSchooxConfirmationOpen: false,
  isLoading: false,
  isComplete: true,
  isSynced: false,
  loadingMessage: '',
  username: '',
  percentage: 0,
  isSchooxPopupClosed: false,
};

export const getAndSyncSchooxUser = () => {
  return createAction(SchooxUserActionType.GET_AND_SYNC_SCHOOX_USER);
};

export const toggleSchooxConfirmation = (isSchooxConfirmationOpen: boolean) => {
  return createAction(SchooxUserActionType.TOGGLE_SCHOOX_CONFIRMATION, {isSchooxConfirmationOpen});
};

export const isSchooxPopupClosedEvent = (isSchooxPopupClosed: boolean) => {
  return createAction(SchooxUserActionType.IS_POPUP_CLOSED, {isSchooxPopupClosed});
};

export const isSchooxLoading = (isLoading: boolean, loadingMessage?: string, percentage?: number) => {
  return createAction(SchooxUserActionType.IS_LOADING, {isLoading, loadingMessage, percentage});
};

export const isSchooxComplete = (isComplete: boolean) => {
  return createAction(SchooxUserActionType.IS_COMPLETE, {isComplete});
};

export const isSchooxUserSynced = (isSynced: boolean) => {
  return createAction(SchooxUserActionType.IS_SYNCED, {isSynced});
};

export const setSchooxPercentage = (percentage: number) => {
  return createAction(SchooxUserActionType.SET_PERCENTAGE, {percentage});
};

export const redirectSchoox = () => {
  return createAction(SchooxUserActionType.REDIRECT_SCHOOX);
};

export const saveSchooxUser = (schooxUser: SchooxUser) => {
  return createAction(SchooxUserActionType.PERSIST_SCHOOX_USER, {schooxUser});
};

type SchooxProfileAction =
  | ReturnType<typeof toggleSchooxConfirmation>
  | ReturnType<typeof isSchooxLoading>
  | ReturnType<typeof isSchooxComplete>
  | ReturnType<typeof isSchooxUserSynced>
  | ReturnType<typeof setSchooxPercentage>
  | ReturnType<typeof getAndSyncSchooxUser>
  | ReturnType<typeof saveSchooxUser>
  | ReturnType<typeof redirectSchoox>
  | ReturnType<typeof isSchooxPopupClosedEvent>;

export default (state: SchooxState = DEFAULT_STATE, action: SchooxProfileAction): SchooxState => {
  switch (action.type) {
    case SchooxUserActionType.PERSIST_SCHOOX_USER:
      return {
        ...state,
        schooxUser: action.payload.schooxUser,
      };
    case SchooxUserActionType.GET_AND_SYNC_SCHOOX_USER:
      return {
        ...state,
      };
    case SchooxUserActionType.TOGGLE_SCHOOX_CONFIRMATION:
      return {
        ...state,
        isSchooxConfirmationOpen: action.payload.isSchooxConfirmationOpen,
      };
    case SchooxUserActionType.IS_POPUP_CLOSED:
      return {
        ...state,
        isSchooxPopupClosed: action.payload.isSchooxPopupClosed,
      };
    case SchooxUserActionType.IS_LOADING:
      return {
        ...state,
        isLoading: action.payload.isLoading,
        percentage: action.payload.percentage,
        loadingMessage: action.payload.loadingMessage,
      };
    case SchooxUserActionType.IS_COMPLETE:
      return {
        ...state,
        isComplete: action.payload.isComplete,
      };
    case SchooxUserActionType.IS_SYNCED:
      return {
        ...state,
        isSynced: action.payload.isSynced,
      };
    case SchooxUserActionType.SET_PERCENTAGE:
      return {
        ...state,
        percentage: action.payload.percentage,
      };
    default:
      return state;
  }
};
